/**
 * Creates an observable state object with subscription capabilities
 * @param {Object} initialState - The initial state object
 * @returns {Object} - Observable state with methods
 */
export function useState(initialState = {}) {
  const listeners = [];
  let state = {...initialState};
  let unsubscribeFn = null;
  
  const observable = {
    /**
     * Get the current state value
     */
    get value() {
      return state;
    },
    
    /**
     * Update the state
     * @param {Object} newState - New values to merge with current state
     */
    update(newState) {
      const oldState = {...state};
      state = {...state, ...newState};
      listeners.forEach(listener => listener(state, oldState));
      return this;
    },
    
    /**
     * Subscribe to state changes
     * @param {Function} listener - Callback function that receives (newState, oldState)
     * @returns {Object} The observable instance for chaining
     */
    subscribe(listener) {
      if (listener) {
        listeners.push(listener);
        unsubscribeFn = () => {
          const index = listeners.indexOf(listener);
          if (index > -1) listeners.splice(index, 1);
        };
      }
      return this;
    },
    
    /**
     * Unsubscribe the listener
     * @returns {Object} The observable instance for chaining
     */
    unsubscribe() {
      if (unsubscribeFn) {
        unsubscribeFn();
        unsubscribeFn = null;
      }
      return this;
    },
    
    /**
     * Reset state to initial value
     * @param {Object} resetState - Optional new initial state
     * @returns {Object} The observable instance for chaining
     */
    reset(resetState = initialState) {
      const oldState = {...state};
      state = {...resetState};
      listeners.forEach(listener => listener(state, oldState));
      return this;
    }
  };
  
  return observable;
}
